/**
 * AX1Demo — Finance Remediation Agent Boardroom Interface
 *
 * AX1 Agentic Demo — Package 2 (AUTHORIZED 2026-09-14)
 *
 * Design constraints:
 *  - NOT a chatbot. Staged governance pipeline visualization.
 *  - 5 stages: UNDERSTAND → REASON → GOVERN → ACT → PROVE
 *  - Each stage shows its Authority source
 *  - AI_UNAVAILABLE is a first-class truthful-degradation state, not an error
 *  - RBAC: finance_manager, cfo, institution_admin, super_admin only
 *  - Tenant-scoped: student picker queries JWT-institution accounts only
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Database,
  Brain,
  Scale,
  ShieldCheck,
  FileCheck,
  Play,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Clock,
  Users,
  Eye,
  RefreshCw,
  ChevronRight,
  Lock,
  Unlock,
  Zap,
} from "lucide-react";
import { PageHeader } from "../components/layout/Shell";
import { ARGOVBadge, ARGOVBadgeStrip } from "../components/shared/ARGOVBadge";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const AX1_ALLOWED_ROLES = new Set([
  "super_admin",
  "institution_admin",
  "finance_manager",
  "cfo",
]);

const STAGE_DEFS = [
  {
    id: "understand",
    label: "UNDERSTAND",
    phase: 1,
    authority: "AcademiaOS Canonical Services",
    authority_detail: "PostgreSQL finance data — deterministic, no fabrication",
    Icon: Database,
    description: "Read canonical finance profile from PostgreSQL",
  },
  {
    id: "reason",
    label: "REASON",
    phase: 2,
    authority: "VEDA-FIN-01 AI Engine",
    authority_detail: "AI reasoning over authoritative snapshot — cannot alter balances",
    Icon: Brain,
    description: "AI analysis of financial position",
  },
  {
    id: "govern",
    label: "GOVERN",
    phase: 3,
    authority: "W1-D InstitutionalAction Framework",
    authority_detail: "Formal proposal with APPROVAL_REQUIRED — no execution without approval",
    Icon: Scale,
    description: "Create formal action proposal",
  },
  {
    id: "act",
    label: "ACT",
    phase: 4,
    authority: "ARGOV + Authorized Human",
    authority_detail: "Execution requires explicit approval — ARGOV boundary enforced",
    Icon: ShieldCheck,
    description: "Human approval and controlled execution",
  },
  {
    id: "prove",
    label: "PROVE",
    phase: 5,
    authority: "Immutable Audit Trail",
    authority_detail: "BusinessTransaction + AuditEvent + EvidenceReference (PostgreSQL)",
    Icon: FileCheck,
    description: "Evidence package — tamper-resistant ledger record",
  },
];

const VARIANT_OPTIONS = [
  { value: "OPEN_CASE", label: "OPEN_CASE — Open FM5 Collection Case" },
  { value: "EXTEND_INVOICE", label: "EXTEND_INVOICE — Extend Invoice Due Date" },
  { value: "CREDIT_MEMO", label: "CREDIT_MEMO — Apply Credit Memo Adjustment" },
  { value: "NO_ACTION", label: "NO_ACTION — No remediation required" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function truncate(str, n = 16) {
  if (!str) return "—";
  return str.length > n ? str.slice(0, n) + "…" : str;
}

function fmtAmount(val, currency = "") {
  if (val == null) return "—";
  const n = parseFloat(val);
  return isNaN(n) ? val : `${currency}${n.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}

function StatusBadge({ status }) {
  const map = {
    READ_COMPLETE:     "bg-emerald-900/50 text-emerald-300 border-emerald-700",
    AWAITING_APPROVAL: "bg-amber-900/50 text-amber-300 border-amber-700",
    PROPOSAL_PENDING:  "bg-amber-900/50 text-amber-300 border-amber-700",
    EXECUTED:          "bg-emerald-900/50 text-emerald-300 border-emerald-700",
    AI_UNAVAILABLE:    "bg-orange-900/50 text-orange-300 border-orange-700",
    APPROVAL_REQUIRED: "bg-amber-900/50 text-amber-300 border-amber-700",
    APPROVED:          "bg-emerald-900/50 text-emerald-300 border-emerald-700",
  };
  const cls = map[status] || "bg-slate-700/50 text-slate-300 border-slate-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-mono ${cls}`}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage indicator dot
// ─────────────────────────────────────────────────────────────────────────────

function StageDot({ status }) {
  if (status === "complete")
    return <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />;
  if (status === "running")
    return <Loader2 className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0" />;
  if (status === "degraded")
    return <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />;
  if (status === "pending")
    return <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />;
  if (status === "active")
    return <Zap className="w-5 h-5 text-blue-400 flex-shrink-0" />;
  if (status === "error")
    return <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />;
  // idle
  return <div className="w-5 h-5 rounded-full border-2 border-slate-600 flex-shrink-0" />;
}

function stageBorderColor(status) {
  if (status === "complete") return "border-l-emerald-500";
  if (status === "running" || status === "active") return "border-l-blue-500";
  if (status === "degraded" || status === "pending") return "border-l-amber-500";
  if (status === "error") return "border-l-red-500";
  return "border-l-slate-700";
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage card
// ─────────────────────────────────────────────────────────────────────────────

function StageCard({ def, status, children }) {
  const { Icon, label, authority, authority_detail, phase } = def;
  return (
    <div className={`bg-slate-900 rounded-lg border border-slate-800 border-l-4 ${stageBorderColor(status)} overflow-hidden`}>
      <div className="flex items-start gap-3 p-4">
        <StageDot status={status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-bold text-white tracking-widest">{label}</span>
              <span className="text-xs text-slate-500">Stage {phase} of 5</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {status !== "idle" && status !== "active" && (
                <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                  status === "complete" ? "bg-emerald-900/40 text-emerald-400" :
                  status === "running" ? "bg-blue-900/40 text-blue-400" :
                  status === "degraded" ? "bg-amber-900/40 text-amber-400" :
                  status === "pending" ? "bg-amber-900/40 text-amber-400" :
                  "bg-slate-800 text-slate-400"
                }`}>
                  {status.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-xs text-slate-500">Authority:</span>
            <span className="text-xs font-mono text-slate-300">{authority}</span>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">{authority_detail}</p>
        </div>
      </div>
      {children && (
        <div className="border-t border-slate-800 px-4 py-3">
          {children}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recent runs table
// ─────────────────────────────────────────────────────────────────────────────

function RecentRunsTable({ institutionId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ax1/runs/${institutionId}`);
      setRuns(data.runs || []);
    } catch (e) {
      toast.error("Failed to load runs: " + formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Loading runs…</span>
      </div>
    );

  if (!runs.length)
    return (
      <div className="text-center py-12 text-slate-500">
        No AX1 runs for this institution yet. Start a new review.
      </div>
    );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-400">{runs.length} run{runs.length !== 1 ? "s" : ""} found</span>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {runs.map((r) => (
        <div key={r.run_id} className="bg-slate-900 rounded-lg border border-slate-800 p-3 grid grid-cols-[1fr_auto] gap-3 items-start">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-slate-300">{truncate(r.run_id, 24)}</span>
              <StatusBadge status={r.status} />
            </div>
            <div className="mt-1 text-xs text-slate-500">
              <span className="mr-3">Phase {r.phase}</span>
              <span className="mr-3">Account: {truncate(r.student_account_id, 20)}</span>
              {r.debt_summary?.net_outstanding && (
                <span>Outstanding: {fmtAmount(r.debt_summary.net_outstanding)}</span>
              )}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              By {r.initiated_by || "—"} · {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
            </div>
          </div>
          <div className="text-right">
            {r.action_id && (
              <div className="text-xs text-slate-600 font-mono">{truncate(r.action_id, 16)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Pipeline
// ─────────────────────────────────────────────────────────────────────────────

function Pipeline({ institutionId }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [status, setStatus] = useState("idle");
  const [runId, setRunId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [readResult, setReadResult] = useState(null);
  const [assistResult, setAssistResult] = useState(null);
  const [proposeResult, setProposeResult] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);
  const [variantOverride, setVariantOverride] = useState("OPEN_CASE");
  const [overrideReason, setOverrideReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load student accounts for this institution
  useEffect(() => {
    api.get(`/finance/student-accounts?institution_id=${institutionId}`)
      .then(({ data }) => setAccounts(data.accounts || []))
      .catch(() => {});
  }, [institutionId]);

  const reset = () => {
    setStatus("idle");
    setRunId(null);
    setActionId(null);
    setActionStatus(null);
    setReadResult(null);
    setAssistResult(null);
    setProposeResult(null);
    setExecuteResult(null);
    setVariantOverride("OPEN_CASE");
    setOverrideReason("");
    setError(null);
  };

  const handleError = useCallback((e, label) => {
    const msg = formatApiError(e?.response?.data?.detail) || e?.message || "Unknown error";
    setError(`${label}: ${msg}`);
    setStatus("error");
    toast.error(`${label}: ${msg}`);
  }, []);

  // Phase 1: READ
  const runPhase1 = async () => {
    if (!selectedAccount) { toast.warning("Select a student account first."); return; }
    setIsLoading(true);
    setStatus("read_running");
    setError(null);
    try {
      const { data } = await api.post(`/ax1/run/${institutionId}/${selectedAccount}`);
      setRunId(data.run_id);
      setReadResult(data);
      setStatus("read_complete");
    } catch (e) {
      handleError(e, "Phase 1 READ");
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 2: ASSIST
  const runPhase2 = async () => {
    setIsLoading(true);
    setStatus("assist_running");
    try {
      const { data } = await api.post(`/ax1/runs/${institutionId}/${runId}/assist`);
      setAssistResult(data);
      setStatus("awaiting_approval");
    } catch (e) {
      handleError(e, "Phase 2 ASSIST");
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 3: PROPOSE
  const runPhase3 = async () => {
    if (!overrideReason.trim()) {
      toast.warning("Enter a reason for the human decision before proposing.");
      return;
    }
    setIsLoading(true);
    setStatus("proposing");
    try {
      const body = { variant_override: variantOverride, override_reason: overrideReason };
      const { data } = await api.post(`/ax1/runs/${institutionId}/${runId}/propose`, body);
      setProposeResult(data);
      setActionId(data.action_id);
      setActionStatus(data.action_status);
      setStatus("proposal_pending");
    } catch (e) {
      handleError(e, "Phase 3 PROPOSE");
    } finally {
      setIsLoading(false);
    }
  };

  // Approve action
  const approveAction = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.post(
        `/institutional-actions/${institutionId}/${actionId}/approve`,
        { notes: overrideReason || "Approved via AX1Demo boardroom interface." }
      );
      setActionStatus(data.status || "APPROVED");
      setStatus("approved");
      toast.success("Action approved.");
    } catch (e) {
      handleError(e, "Approval");
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 4: EXECUTE
  const runPhase4 = async () => {
    setIsLoading(true);
    setStatus("executing");
    try {
      const { data } = await api.post(`/ax1/runs/${institutionId}/${runId}/execute`);
      setExecuteResult(data.execution_result);
      setStatus("executed");
      toast.success("Action executed. Evidence chain complete.");
    } catch (e) {
      handleError(e, "Phase 4 EXECUTE");
    } finally {
      setIsLoading(false);
    }
  };

  // Compute per-stage status
  function stageStatus(stageId) {
    const s = status;
    switch (stageId) {
      case "understand":
        if (["executed", "executing", "approved", "proposal_pending", "proposing",
             "awaiting_approval", "assist_running", "read_complete"].includes(s)) return "complete";
        if (s === "read_running") return "running";
        return "idle";
      case "reason":
        if (["executed", "executing", "approved", "proposal_pending", "proposing",
             "awaiting_approval"].includes(s)) {
          return assistResult?.assist_status === "AI_UNAVAILABLE" ? "degraded" : "complete";
        }
        if (s === "assist_running") return "running";
        return s === "read_complete" ? "active" : "idle";
      case "govern":
        if (["executed", "executing", "approved", "proposal_pending"].includes(s)) return "complete";
        if (s === "proposing") return "running";
        return s === "awaiting_approval" ? "active" : "idle";
      case "act":
        if (["executed"].includes(s)) return "complete";
        if (["executing", "approved", "proposal_pending"].includes(s)) return "pending";
        return "idle";
      case "prove":
        if (s === "executed") return "complete";
        return "idle";
      default:
        return "idle";
    }
  }

  const aiUnavailable = assistResult?.assist_status === "AI_UNAVAILABLE";
  const ds = readResult?.debt_summary;

  return (
    <div className="space-y-1">
      {/* Student picker */}
      {status === "idle" && (
        <div className="bg-slate-900 rounded-lg border border-slate-800 p-4 mb-4" data-testid="ax1-student-picker">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-white">Select Student Account</span>
            <span className="text-xs text-slate-500">· Scoped to your institution</span>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="ax1-account-select"
              >
                <option value="">— Choose student account —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.display_name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={runPhase1}
              disabled={!selectedAccount || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors"
              data-testid="ax1-start-btn"
            >
              <Play className="w-4 h-4" />
              START REVIEW
            </button>
          </div>
        </div>
      )}

      {/* Reset + error */}
      {status !== "idle" && (
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-slate-500 font-mono">
            Run: {runId ? truncate(runId, 24) : "—"}
          </div>
          <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> New Review
          </button>
        </div>
      )}
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-md px-3 py-2 text-sm text-red-300 mb-2" data-testid="ax1-error">
          {error}
        </div>
      )}

      {/* ── Stage 1: UNDERSTAND ── */}
      <StageCard def={STAGE_DEFS[0]} status={stageStatus("understand")}>
        {stageStatus("understand") === "complete" && ds && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="ax1-stage-understand">
            <div className="text-slate-400">Net Outstanding</div>
            <div className="font-mono text-white font-semibold">{fmtAmount(ds.net_outstanding)}</div>
            <div className="text-slate-400">Total Charged</div>
            <div className="font-mono text-slate-300">{fmtAmount(ds.total_charged)}</div>
            <div className="text-slate-400">Total Paid</div>
            <div className="font-mono text-slate-300">{fmtAmount(ds.total_paid)}</div>
            <div className="text-slate-400">Aging Status</div>
            <div className="font-mono text-slate-300">{ds.aging_summary}</div>
            <div className="text-slate-400">Tool Calls</div>
            <div className="font-mono text-slate-300">
              ax1_read_finance_profile (READ), ax1_compute_collectible_debt (READ)
            </div>
          </div>
        )}
        {stageStatus("understand") === "running" && (
          <div className="flex items-center gap-2 text-sm text-blue-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            Reading canonical PostgreSQL finance data…
          </div>
        )}
      </StageCard>

      {/* Connector */}
      <div className="flex justify-center"><div className="w-px h-3 bg-slate-700" /></div>

      {/* ── Stage 2: REASON ── */}
      <StageCard def={STAGE_DEFS[1]} status={stageStatus("reason")}>
        {stageStatus("reason") === "active" && (
          <button
            onClick={runPhase2}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-xs font-medium rounded-md transition-colors"
            data-testid="ax1-run-assist-btn"
          >
            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
            Run AI Analysis
          </button>
        )}
        {stageStatus("reason") === "running" && (
          <div className="flex items-center gap-2 text-sm text-blue-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            Invoking VEDA-FIN-01…
          </div>
        )}
        {["complete", "degraded"].includes(stageStatus("reason")) && assistResult && (
          <div data-testid="ax1-stage-reason">
            {aiUnavailable ? (
              <div className="bg-amber-950/40 border border-amber-800/60 rounded-md p-3 space-y-1">
                <div className="flex items-center gap-2 text-amber-300 font-semibold text-xs">
                  <AlertTriangle className="w-4 h-4" />
                  TRUTHFUL DEGRADATION — AI UNAVAILABLE
                </div>
                <p className="text-xs text-amber-200/80 leading-relaxed">
                  The AI reasoning service is currently unavailable. This is a governance
                  feature, not an error. The canonical finance data was retrieved successfully.
                  Human review of the balance is required before any action can be proposed.
                </p>
                <div className="text-xs font-mono text-amber-300/70 mt-1">
                  Event ID: {assistResult.event_id}
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-xs">
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  <span className="text-slate-400">Confidence</span>
                  <span className="font-mono text-white">{assistResult.reasoning?.confidence}</span>
                  <span className="text-slate-400">Recommendation</span>
                  <span className="font-mono text-white">{assistResult.reasoning?.recommendation_variant}</span>
                </div>
                {assistResult.reasoning?.reasoning && (
                  <div className="mt-2 text-slate-300 leading-relaxed border-t border-slate-800 pt-2">
                    {assistResult.reasoning.reasoning}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </StageCard>

      <div className="flex justify-center"><div className="w-px h-3 bg-slate-700" /></div>

      {/* ── Stage 3: GOVERN ── */}
      <StageCard def={STAGE_DEFS[2]} status={stageStatus("govern")}>
        {stageStatus("govern") === "active" && (
          <div className="space-y-3" data-testid="ax1-stage-govern">
            {aiUnavailable && (
              <div className="text-xs text-amber-300/80 border-l-2 border-amber-600 pl-2">
                AI is unavailable. You are the decision authority. Select the action
                variant and provide your reasoning below.
              </div>
            )}
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center text-xs">
              <span className="text-slate-400">Action Variant</span>
              <select
                value={variantOverride}
                onChange={(e) => setVariantOverride(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="ax1-variant-select"
              >
                {VARIANT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="text-slate-400">Decision Reason</span>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="State your basis for this decision…"
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="ax1-override-reason"
              />
            </div>
            <button
              onClick={runPhase3}
              disabled={isLoading || !overrideReason.trim()}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-xs font-medium rounded-md transition-colors"
              data-testid="ax1-propose-btn"
            >
              {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scale className="w-3 h-3" />}
              Propose Action
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
        {stageStatus("govern") === "running" && (
          <div className="flex items-center gap-2 text-sm text-blue-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            Creating InstitutionalAction (APPROVAL_REQUIRED)…
          </div>
        )}
        {stageStatus("govern") === "complete" && proposeResult && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="ax1-govern-result">
            <span className="text-slate-400">Action ID</span>
            <span className="font-mono text-white">{truncate(proposeResult.action_id, 24)}</span>
            <span className="text-slate-400">Variant</span>
            <span className="font-mono text-white">{proposeResult.variant}</span>
            <span className="text-slate-400">Status</span>
            <StatusBadge status={proposeResult.action_status} />
          </div>
        )}
      </StageCard>

      <div className="flex justify-center"><div className="w-px h-3 bg-slate-700" /></div>

      {/* ── Stage 4: ACT ── */}
      <StageCard def={STAGE_DEFS[3]} status={stageStatus("act")}>
        {(stageStatus("act") === "pending" || stageStatus("act") === "running") && (
          <div className="space-y-3" data-testid="ax1-stage-act">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
              <span className="text-slate-400">Action ID</span>
              <span className="font-mono text-white">{truncate(actionId, 24)}</span>
              <span className="text-slate-400">Current Status</span>
              <StatusBadge status={actionStatus} />
            </div>
            <div className="flex gap-2">
              {actionStatus === "APPROVAL_REQUIRED" && (
                <button
                  onClick={approveAction}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-medium rounded-md transition-colors"
                  data-testid="ax1-approve-btn"
                >
                  {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                  Approve Action
                </button>
              )}
              <button
                onClick={runPhase4}
                disabled={isLoading || actionStatus !== "APPROVED" || status === "executing"}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-colors"
                data-testid="ax1-execute-btn"
              >
                {isLoading && status === "executing"
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : actionStatus !== "APPROVED"
                    ? <Lock className="w-3 h-3" />
                    : <Zap className="w-3 h-3" />}
                Execute Action
                {actionStatus !== "APPROVED" && (
                  <span className="text-blue-300/50 text-xs">(needs approval)</span>
                )}
              </button>
            </div>
          </div>
        )}
        {stageStatus("act") === "complete" && executeResult && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="ax1-act-result">
            <span className="text-slate-400">Variant Executed</span>
            <span className="font-mono text-white">{executeResult.variant}</span>
            <span className="text-slate-400">Domain Record</span>
            <span className="font-mono text-white">{truncate(executeResult.record_id || "none", 24)}</span>
          </div>
        )}
      </StageCard>

      <div className="flex justify-center"><div className="w-px h-3 bg-slate-700" /></div>

      {/* ── Stage 5: PROVE ── */}
      <StageCard def={STAGE_DEFS[4]} status={stageStatus("prove")}>
        {stageStatus("prove") === "complete" && executeResult && (
          <div className="space-y-2" data-testid="ax1-stage-prove">
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
              <span className="text-slate-400">BusinessTransaction</span>
              <span className="font-mono text-emerald-300">{executeResult.business_transaction_id}</span>
              <span className="text-slate-400">AuditEvent</span>
              <span className="font-mono text-emerald-300">{executeResult.audit_event_id}</span>
              <span className="text-slate-400">EvidenceReference</span>
              <span className="font-mono text-emerald-300">{executeResult.evidence_reference_id}</span>
            </div>
            {executeResult.evidence_hash && (
              <div className="mt-2 border-t border-slate-800 pt-2 text-xs">
                <span className="text-slate-400">Integrity Hash</span>
                <div className="font-mono text-slate-400 mt-0.5 break-all">{executeResult.evidence_hash}</div>
              </div>
            )}
            <div className="mt-2 bg-emerald-950/40 border border-emerald-800/40 rounded-md px-3 py-2 text-xs text-emerald-300">
              Evidence chain complete. This run is now immutable. All records are in
              PostgreSQL audit tables with cryptographic integrity.
            </div>
          </div>
        )}
      </StageCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function AX1Demo() {
  const { user } = useAuth();
  const [mode, setMode] = useState("new_review");

  if (!user || !AX1_ALLOWED_ROLES.has(user.role)) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        Access restricted. Requires finance_manager, cfo, institution_admin, or super_admin role.
      </div>
    );
  }

  const institutionId = user.institution_id;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Header */}
        <div className="space-y-1" data-testid="ax1-demo-header">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-950/60 border border-blue-800/50 text-blue-300 text-xs font-mono">
              <Zap className="w-3 h-3" />
              AX1 FINAL AGENTIC DEMO
            </div>
            <ARGOVBadge type="AI_GOVERNED" />
            <ARGOVBadge type="APPROVAL_REQUIRED" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Finance Remediation Agent
          </h1>
          <p className="text-sm text-slate-400">
            Governed agentic chain: User request → VEDA-FIN-01 → ARGOV boundary →
            human approval → controlled execution → evidence package.
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
            <span>Institution: <span className="font-mono text-slate-400">{institutionId}</span></span>
            <span>·</span>
            <span>Actor: <span className="font-mono text-slate-400">{user.email}</span></span>
          </div>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 p-1 bg-slate-900 rounded-lg border border-slate-800 w-fit" data-testid="ax1-mode-tabs">
          <button
            onClick={() => setMode("new_review")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === "new_review"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
            data-testid="ax1-tab-new-review"
          >
            <Play className="w-3 h-3 inline mr-1.5" />
            START NEW REVIEW
          </button>
          <button
            onClick={() => setMode("recent_runs")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === "recent_runs"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
            data-testid="ax1-tab-recent-runs"
          >
            <Eye className="w-3 h-3 inline mr-1.5" />
            RECENT RUNS
          </button>
        </div>

        {/* Content */}
        {mode === "new_review" ? (
          <Pipeline institutionId={institutionId} />
        ) : (
          <div data-testid="ax1-recent-runs">
            <RecentRunsTable institutionId={institutionId} />
          </div>
        )}

        {/* Governance footnote */}
        <div className="border-t border-slate-800/60 pt-4 text-xs text-slate-600 space-y-0.5">
          <div>AX1-INV-01: Execution blocked unless InstitutionalAction.status == APPROVED</div>
          <div>AX1-INV-04: Finance data read from canonical PostgreSQL — no mocks</div>
          <div>AX1-INV-05: Same agent code, tenant isolation via JWT scope only</div>
        </div>
      </div>
    </div>
  );
}
